import { log } from "@grafit/shared/observability/sentry-front";
import { isProdOrPreviewEnv } from "@grafit/shared/utils/envs.front";

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// Second removal path next to the kill switch /sw.js: index.html never comes from the worker, so this code runs
// even if the installed worker misbehaves.
// Don't import sw-cache.ts here: a module shared with the app would move into a chunk that /sw.js can't import.
async function removeServiceWorker(): Promise<void> {
  const [registrations, cacheNames] = await Promise.all([navigator.serviceWorker.getRegistrations(), caches.keys()]);
  await Promise.all([
    ...registrations.map((registration) => registration.unregister()),
    ...cacheNames.filter((name) => name.startsWith("assets-")).map((name) => caches.delete(name)),
  ]);
}

/** Production and Netlify previews (same setup), not local dev/builds. Playwright blocks service workers. */
export function registerServiceWorker(): void {
  if (!isProdOrPreviewEnv() || !("serviceWorker" in navigator)) {
    return;
  }
  const isKillSwitchOn = import.meta.env["VITE_SW_KILL_SWITCH"] === "true";
  // After load, so precaching doesn't compete with the startup requests.
  window.addEventListener(
    "load",
    () => {
      const action = isKillSwitchOn ? removeServiceWorker() : navigator.serviceWorker.register("/sw.js");
      action.catch((error: unknown) => {
        log(`Service worker ${isKillSwitchOn ? "removal" : "registration"} failed: ${getErrorMessage(error)}`, "warn");
      });
    },
    { once: true },
  );
}

let hasRequestedUpdate = false;

/** Lets the new build's service worker precache before the app reloads into that build. */
export function requestServiceWorkerUpdate(): void {
  if (hasRequestedUpdate || !("serviceWorker" in navigator)) {
    return;
  }
  hasRequestedUpdate = true;
  navigator.serviceWorker
    .getRegistration()
    .then((registration) => registration?.update())
    .catch((error: unknown) => {
      log(`Service worker update failed: ${getErrorMessage(error)}`, "warn");
    });
}
